import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Button,
  Tag,
  Modal,
  Tabs,
  Avatar,
  Empty,
  Tooltip,
} from 'antd';
import {
  PictureOutlined,
  FileTextOutlined,
  SaveOutlined,
  RobotOutlined,
  EditOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { NodeDetailResponse, LifetreeDetailResponse } from '../../api/models/Lifetree';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface NodeBasicInfoTabProps {
  form: any;
  nodeDetail: NodeDetailResponse;
  lifetreeDetail: LifetreeDetailResponse | null;
  onFormChange: () => void;
  onSave: () => Promise<void>;
  saving: boolean;
  hasChanges: boolean;
  onGenerateSummary: () => Promise<void>;
  generatingSummary: boolean;
  onSetCoverImage: (imageUrl: string) => Promise<void>;
  nodeImages: any[];
  aiConcept: any | null;
  isGeneratingConcept: boolean;
  isGeneratingNodeImage: boolean;
  onGenerateNodeImageConcept: () => Promise<void>;
  onGenerateNodeImage: (description: string) => Promise<void>;
}

const NodeBasicInfoTab: React.FC<NodeBasicInfoTabProps> = ({
  form,
  nodeDetail,
  lifetreeDetail,
  onFormChange,
  onSave,
  saving,
  hasChanges,
  onGenerateSummary,
  generatingSummary,
  onSetCoverImage,
  nodeImages,
  aiConcept,
  isGeneratingConcept,
  isGeneratingNodeImage,
  onGenerateNodeImageConcept,
  onGenerateNodeImage,
}) => {
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [settingCover, setSettingCover] = useState(false);

  // Collect all state images
  const stateImages = nodeDetail.characterStates.flatMap(cs => {
    const charName = lifetreeDetail?.characters.find(c => c.id === cs.characterId)?.name || cs.name;
    return (cs.images || []).map(img => ({
      url: img.url,
      label: charName,
      stateName: cs.name,
      avatar: cs.imageUrl,
      width: img.width,
      height: img.height,
    }));
  });

  // Collect all scenario images
  const scenarioImages = nodeDetail.scenarios.flatMap(sc =>
    (sc.images || []).map(img => ({
      url: img.url,
      label: sc.name,
      stateName: undefined,
      avatar: undefined,
      width: img.width,
      height: img.height,
    }))
  );

  // Collect all generated node images
  const nodeGeneratedImages = (nodeImages || []).map(img => ({
    url: img.url,
    label: 'Ảnh Node',
    stateName: undefined,
    avatar: undefined,
    width: img.width,
    height: img.height,
  }));

  const handleOpenPicker = () => {
    setSelectedUrl(nodeDetail.imageUrl || null);
    setCoverPickerOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedUrl) return;
    setSettingCover(true);
    try {
      await onSetCoverImage(selectedUrl);
      setCoverPickerOpen(false);
    } finally {
      setSettingCover(false);
    }
  };

  const ImageGrid = ({ images }: { images: { url: string; label: string; stateName?: string; avatar?: string | null; width?: number; height?: number }[] }) => {
    if (images.length === 0) {
      return <Empty description="Chưa có ảnh" style={{ margin: '24px 0' }} />;
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
        {images.map((img, idx) => {
          const isSelected = selectedUrl === img.url;
          // Compute display size: max width 200, max height 180, preserve ratio
          const maxW = 200;
          const maxH = 180;
          let dispW = maxW;
          let dispH = maxH;
          if (img.width && img.height) {
            const ratio = img.width / img.height;
            if (ratio >= 1) {
              dispW = maxW;
              dispH = Math.round(maxW / ratio);
            } else {
              dispH = maxH;
              dispW = Math.round(maxH * ratio);
            }
          }
          const resLabel = img.width && img.height ? `${img.width}×${img.height}` : '';
          return (
            <div
              key={idx}
              onClick={() => setSelectedUrl(img.url)}
              style={{
                position: 'relative',
                width: dispW,
                cursor: 'pointer',
                borderRadius: 8,
                border: isSelected ? '2px solid #1890ff' : '2px solid transparent',
                boxShadow: isSelected ? '0 0 0 2px #91caff' : '0 1px 4px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                background: '#f5f5f5',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}
            >
              <img
                src={img.url}
                alt={img.label}
                style={{ width: dispW, height: dispH, objectFit: 'contain', display: 'block', background: '#f0f0f0' }}
              />
              <div style={{ padding: '5px 8px', background: '#fff' }}>
                <Space size={4} style={{ width: '100%' }}>
                  {img.avatar !== undefined && (
                    <Avatar src={img.avatar} icon={<UserOutlined />} size={14} style={{ flexShrink: 0 }} />
                  )}
                  <Text style={{ fontSize: 11, flex: 1, minWidth: 0 }} ellipsis={{ tooltip: img.label }}>
                    {img.label}
                  </Text>
                </Space>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {img.stateName && (
                    <Text type="secondary" style={{ fontSize: 10 }}>{img.stateName}</Text>
                  )}
                  {resLabel && (
                    <Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>{resLabel}</Text>
                  )}
                </div>
              </div>
              {isSelected && (
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  background: '#1890ff', borderRadius: '50%',
                  width: 20, height: 20,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircleOutlined style={{ color: '#fff', fontSize: 12 }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Form form={form} layout="vertical" onValuesChange={onFormChange}>
      {/* Tab Header Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20
      }}>
        <Space>
          <Title level={5} style={{ margin: 0 }}>Thông tin cơ bản</Title>
          {hasChanges && (
            <Tag color="volcano">Chưa lưu</Tag>
          )}
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!hasChanges}
            onClick={onSave}
          >
            Lưu thay đổi
          </Button>
        </Space>
      </div>

      {/* ── Section 1: Metadata + Description ── */}
      <Row gutter={[24, 0]} align="top" style={{ marginBottom: 20 }}>
        {/* Thumbnail */}
        <Col flex="none">
          <Tooltip title="Chọn ảnh bìa">
            <div
              onClick={handleOpenPicker}
              style={{
                width: 90,
                height: 90,
                borderRadius: 8,
                border: '1px dashed #d9d9d9',
                background: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              {nodeDetail.imageUrl ? (
                <>
                  <img src={nodeDetail.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(0,0,0,0.35)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                    className="cover-hover-overlay"
                    onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                    onMouseLeave={e => (e.currentTarget.style.opacity = '0')}
                  >
                    <EditOutlined style={{ color: '#fff', fontSize: 20 }} />
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#bfbfbf' }}>
                  <PictureOutlined style={{ fontSize: 22 }} />
                  <div style={{ fontSize: 10, marginTop: 4 }}>Chọn ảnh</div>
                </div>
              )}
            </div>
          </Tooltip>
        </Col>

        {/* Title + Level/EXP/Tags */}
        <Col flex="auto">
          <Form.Item
            name="title"
            label="Tên Node"
            rules={[{ required: true, message: 'Vui lòng nhập tên node' }]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="Nhập tên node..." />
          </Form.Item>
          <Row gutter={[12, 0]}>
            <Col span={6}>
              <Form.Item name="level" label="Level" style={{ marginBottom: 0 }}>
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="requiredExp" label="EXP Yêu cầu" style={{ marginBottom: 0 }}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="tags" label="Tags" style={{ marginBottom: 0 }}>
                <Select mode="tags" placeholder="Thêm tags..." style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        {/* Description */}
        <Col flex="none" style={{ width: '40%' }}>
          <Form.Item name="description" label="Mô tả (hiển thị cho người chơi)" style={{ marginBottom: 0 }}>
            <TextArea
              placeholder="Mô tả ngắn gọn, hấp dẫn — đây là thứ người chơi nhìn thấy đầu tiên..."
              rows={5}
              showCount
              maxLength={500}
              style={{ resize: 'none' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider style={{ margin: '0 0 20px' }} />

      {/* ── Section 2: Summary ── */}
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6
          }}>
            <Space>
              <FileTextOutlined style={{ color: '#52c41a' }} />
              <Text strong>Summary</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>(Tóm tắt sau khi hoàn thiện)</Text>
            </Space>
            <Button
              size="small"
              icon={<RobotOutlined />}
              onClick={onGenerateSummary}
              loading={generatingSummary}
              disabled={nodeDetail.scenarios.length === 0}
            >
              Tự động tóm tắt (AI)
            </Button>
          </div>
          <Form.Item name="summary" style={{ marginBottom: 0 }}>
            <TextArea
              placeholder="Tóm tắt những gì đã xảy ra trong node. AI sẽ đọc field này để hiểu bối cảnh khi tạo nội dung cho các node tiếp theo."
              rows={6}
              showCount
              maxLength={2000}
              style={{ resize: 'vertical' }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Cover Image Picker Modal */}
      <Modal
        title={
          <Space>
            <PictureOutlined style={{ color: '#1890ff' }} />
            <span>Chọn ảnh bìa Node</span>
          </Space>
        }
        open={coverPickerOpen}
        onCancel={() => setCoverPickerOpen(false)}
        width={760}
        footer={[
          <Button key="cancel" onClick={() => setCoverPickerOpen(false)}>
            Hủy
          </Button>,
          <Button
            key="confirm"
            type="primary"
            disabled={!selectedUrl || selectedUrl === nodeDetail.imageUrl}
            loading={settingCover}
            onClick={handleConfirm}
          >
            Đặt làm ảnh bìa
          </Button>,
        ]}
      >
        <Tabs
          items={[
            {
              key: 'states',
              label: `Ảnh nhân vật (${stateImages.length})`,
              children: (
                <div style={{ maxHeight: '55vh', overflowY: 'auto', padding: '4px 2px' }}>
                  <ImageGrid images={stateImages} />
                </div>
              ),
            },
            {
              key: 'scenarios',
              label: `Ảnh scenario (${scenarioImages.length})`,
              children: (
                <div style={{ maxHeight: '55vh', overflowY: 'auto', padding: '4px 2px' }}>
                  <ImageGrid images={scenarioImages} />
                </div>
              ),
            },
            {
              key: 'generated',
              label: `Tạo ảnh mới (${nodeGeneratedImages.length})`,
              children: (
                <div style={{ maxHeight: '55vh', overflowY: 'auto', padding: '4px 2px' }}>
                  <div style={{ marginBottom: 16, padding: 12, background: '#f9f9f9', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text strong>Tạo ảnh Node bằng AI</Text>
                      <Button 
                        type="primary" 
                        size="small" 
                        icon={<RobotOutlined />} 
                        onClick={onGenerateNodeImageConcept}
                        loading={isGeneratingConcept}
                      >
                        AI đề xuất concept
                      </Button>
                    </div>
                    
                    {aiConcept && (
                      <div style={{ padding: 12, background: '#fff', borderRadius: 4, border: '1px solid #e8e8e8' }}>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Mô tả nội dung (Việt):</Text>
                          <div style={{ padding: '4px 8px', background: '#fafafa', borderRadius: 4, marginTop: 4 }}>
                            <Text style={{ fontSize: 13 }}>{aiConcept.refinedVisualDescription || aiConcept.description}</Text>
                          </div>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Nhân vật xuất hiện:</Text>
                          <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {aiConcept.involvedCharacterIds?.map((charId: string) => {
                              const char = lifetreeDetail?.characters.find(c => c.id === charId);
                              return (
                                <Tag key={charId} icon={<UserOutlined />} color="blue">
                                  {char?.name || charId}
                                </Tag>
                              );
                            })}
                            {(!aiConcept.involvedCharacterIds || aiConcept.involvedCharacterIds.length === 0) && (
                              <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>Không xác định</Text>
                            )}
                          </div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Technical Prompt (English):</Text>
                          <div style={{ padding: '4px 8px', background: '#fafafa', borderRadius: 4, marginTop: 4 }}>
                            <Text style={{ fontSize: 11, fontStyle: 'italic' }}>{aiConcept.technicalPrompt}</Text>
                          </div>
                        </div>
                        <Button 
                          type="primary" 
                          block 
                          icon={<PictureOutlined />} 
                          onClick={() => onGenerateNodeImage(aiConcept.technicalPrompt || aiConcept.description)}
                          loading={isGeneratingNodeImage}
                        >
                          Bắt đầu tạo ảnh (1:1 WebP)
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <Divider orientation="left" style={{ margin: '12px 0' }}><Text type="secondary">Ảnh đã tạo</Text></Divider>
                  <ImageGrid images={nodeGeneratedImages} />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </Form>
  );
};

export default NodeBasicInfoTab;
